import { Badge, Banner, Button, Checkbox, Field, Input, InputArea, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { providerBrand } from '../lib/brandLogos';
import { DangerButton } from '../components/ui/DangerButton';
import { ICONS } from '../components/ui/icons';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { SkeletonText } from '../components/ui/Skeleton';

interface Rule { keywords: string; reply: string; match: 'contains' | 'exact' }
interface Config {
  active: boolean; rules: Rule[];
  awayEnabled: boolean; awayMessage: string; window: { startHour: number; endHour: number; days: number[] }; cooldownMinutes: number;
  aiEnabled: boolean; aiInstructions: string; aiKnowledge: string; pauseHours: number; handoffTags: string;
}
interface Connection {
  name: string; displayName: string | null; status: string; provider: string; interactiveCampaignEnabled: boolean;
  config: (Partial<Config> & { id: string }) | null;
  last7d: Record<string, number>;
}

const DEFAULT: Config = {
  active: true, rules: [],
  awayEnabled: false, awayMessage: 'Olá! Nosso atendimento é de segunda a sexta, das 8h às 18h. Respondemos assim que voltarmos. 🙂',
  window: { startHour: 8, endHour: 18, days: [1, 2, 3, 4, 5] }, cooldownMinutes: 240,
  aiEnabled: false, aiInstructions: '', aiKnowledge: '', pauseHours: 12, handoffTags: 'atendimento-humano',
};
const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const KIND_LABEL: Record<string, string> = { RULE: 'Palavra-chave', AWAY: 'Fora do horário', AI: 'IA respondeu', HANDOFF: 'Passou para a equipe' };
const selectStyle = { height: 32, border: '1px solid var(--text-faint)', borderRadius: 6, padding: '0 6px' } as const;

/** WhatsApp › Respostas automáticas: palavra-chave, ausência e IA por conexão */
export function RespostasAutomaticasPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<{ aiAvailable: boolean; connections: Connection[] } | null>(null);
  const [editing, setEditing] = useState<Connection | null>(null);
  const [form, setForm] = useState<Config>(DEFAULT);
  const [busy, setBusy] = useState(false);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<{ kind: string | null; reply: string | null; error?: string } | null>(null);
  const canEdit = can('campaigns:write');

  const load = useCallback(async () => {
    const res = await fetchApi('/api/respostas-automaticas');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const open = (c: Connection) => {
    const cfg = c.config;
    setForm(cfg ? {
      ...DEFAULT, ...cfg,
      rules: (cfg.rules as Rule[]) || [],
      awayMessage: cfg.awayMessage || DEFAULT.awayMessage,
      window: (cfg.window as Config['window']) || DEFAULT.window,
      aiInstructions: cfg.aiInstructions || '', aiKnowledge: cfg.aiKnowledge || '', handoffTags: cfg.handoffTags ?? '',
    } : DEFAULT);
    setTestText(''); setTestResult(null);
    setEditing(c);
  };

  const save = async (patch?: Partial<Config>, target?: Connection) => {
    const conn = target || editing;
    if (!conn) return;
    setBusy(true);
    try {
      const res = await fetchApi(`/api/respostas-automaticas/${encodeURIComponent(conn.name)}`, { method: 'PUT', body: JSON.stringify({ ...form, ...patch }) });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.message || 'Não foi possível salvar');
      if (!patch) { toast.success('Respostas automáticas salvas'); setEditing(null); }
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar');
    } finally {
      setBusy(false);
    }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      const res = await fetchApi('/api/respostas-automaticas/simular', { method: 'POST', body: JSON.stringify({ text: testText, config: form }) });
      const out = await res.json();
      if (!res.ok) throw new Error(out.message || 'Falha na simulação');
      setTestResult(out);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha na simulação');
    } finally {
      setBusy(false);
    }
  };

  const setRule = (i: number, patch: Partial<Rule>) => setForm({ ...form, rules: form.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;

  if (editing) {
    return (
      <FormPage
        backTo="/respostas-automaticas"
        backLabel="Respostas automáticas"
        onBack={() => setEditing(null)}
        title={`Respostas automáticas · ${editing?.displayName || editing?.name}`}
        description="A ordem é: palavra-chave, depois fora do horário, depois IA."
        submitLabel={'Salvar'}
        submitting={busy}
        onSubmit={() => save()}
        onCancel={() => setEditing(null)}
      >
        <FormSection title="Regras">


        <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
          A ordem é: palavra-chave, depois fora do horário, depois IA.
        </div>
        <div className="space-y-6" >
          <section>
            <h4 className="text-base font-semibold" style={{ fontSize: 15 }}>Palavras-chave</h4>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              Separe variações por vírgula. "Contém" acha a palavra no meio da frase; "exata" só a mensagem inteira.
            </div>
            <div className="space-y-3">
              {form.rules.map((r, i) => (
                <div key={i} className="grid gap-2 md:grid-cols-[1fr_2fr_auto]" style={{ alignItems: 'end' }}>
                  <div>
                    <Field label={i === 0 ? 'Se a mensagem tiver' : undefined}>
                      <Input onChange={(e) => setRule(i, { keywords: e.target.value })} aria-label="Palavras" placeholder="preço, valor, quanto custa" value={r.keywords} />
                    </Field>
                    <select
                      aria-label="Tipo de comparação"
                      value={r.match}
                      style={{ ...selectStyle, marginTop: 4 }}
                      onChange={(e) => setRule(i, { match: e.target.value as Rule['match'] })}
                    >
                      <option value="contains">Contém</option><option value="exact">Exata</option>
                    </select>
                  </div>
                  <Field label={i === 0 ? 'Responder' : undefined}>
                    <InputArea onChange={(e) => setRule(i, { reply: e.target.value })} aria-label="Resposta" rows={2} value={r.reply} />
                  </Field>
                  <DangerButton
                    variant="dangerOutline"
                    aria-label={'Remover'}
                    onClick={() => setForm({ ...form, rules: form.rules.filter((_, j) => j !== i) })}
                  >
                    <ICONS.Trash size={16} />
                  </DangerButton>
                </div>
              ))}
              <Button variant="secondary" icon={ICONS.Plus} onClick={() => setForm({ ...form, rules: [...form.rules, { keywords: '', reply: '', match: 'contains' }] })}>
                Adicionar palavra-chave
              </Button>
            </div>
          </section>

          <section>
            <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, awayEnabled: checked })} checked={form.awayEnabled} label={<>
              Mensagem fora do horário de atendimento
            </>} />
            {form.awayEnabled && (
              <div className="space-y-3" style={{ marginTop: 10 }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <div style={{ fontSize: 13 }}>Atendimento das</div>
                  <select
                    aria-label="Início"
                    value={form.window.startHour}
                    style={selectStyle}
                    onChange={(e) => setForm({ ...form, window: { ...form.window, startHour: Number(e.target.value) } })}
                  >
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                  <div style={{ fontSize: 13 }}>às</div>
                  <select
                    aria-label="Fim"
                    value={form.window.endHour}
                    style={selectStyle}
                    onChange={(e) => setForm({ ...form, window: { ...form.window, endHour: Number(e.target.value) } })}
                  >
                    {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                  {DAYS.map((d, i) => {
                    const on = form.window.days.includes(i);
                    return (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setForm({ ...form, window: { ...form.window, days: on ? form.window.days.filter((x) => x !== i) : [...form.window.days, i].sort() } })}
                        style={{ padding: '4px 10px', borderRadius: 14, fontSize: 12, border: `1px solid ${on ? 'var(--brand-text)' : 'var(--border-strong)'}`, background: on ? 'var(--tint-green)' : 'var(--surface)', color: on ? 'var(--brand-text)' : 'var(--text-muted)', cursor: 'pointer' }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                <InputArea label="Mensagem" onChange={(e) => setForm({ ...form, awayMessage: e.target.value })} rows={2} value={form.awayMessage} />
                <Field label="Repetir para o mesmo contato no máximo a cada (minutos)">
                  <Input onChange={(e) => setForm({ ...form, cooldownMinutes: parseInt(e.target.value) || 240 })} type="number" min={30} max={1440} value={String(form.cooldownMinutes)} />
                </Field>
              </div>
            )}
          </section>

          <section>
            <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, aiEnabled: checked })} disabled={!data.aiAvailable} checked={form.aiEnabled} labelTooltip={data.aiAvailable ? 'Usa a IA da plataforma, com a cota diária da organização.' : 'A IA da plataforma não está ativa: peça ao administrador.'} label={<>
              IA responde as outras mensagens
            </>} />
            {form.aiEnabled && (
              <div className="space-y-3" style={{ marginTop: 10 }}>
                <InputArea label="Instruções" onChange={(e) => setForm({ ...form, aiInstructions: e.target.value })} rows={3} placeholder="Você é a Ana, atendente da Loja X. Tire dúvidas e ajude a fechar o pedido. Seja breve e simpática." value={form.aiInstructions} />
                <InputArea label="Base de conhecimento" description={`${form.aiKnowledge.length.toLocaleString('pt-BR')} / 12.000 caracteres. A IA não inventa o que não estiver aqui.`} onChange={(e) => setForm({ ...form, aiKnowledge: e.target.value })} rows={6} placeholder="Preços, horários, endereço, formas de pagamento, prazos de entrega, perguntas frequentes…" value={form.aiKnowledge} />
                <Field label="Depois de passar para a equipe, ficar em silêncio com o contato por (horas)">
                  <Input onChange={(e) => setForm({ ...form, pauseHours: parseInt(e.target.value) || 12 })} type="number" min={1} max={72} value={String(form.pauseHours)} />
                </Field>
                <Field label="Etiquetas no Chatwoot ao passar para a equipe" description="Se a organização usa Chatwoot, o contato recebe estas etiquetas (separe por vírgula).">
                  <Input onChange={(e) => setForm({ ...form, handoffTags: e.target.value })} value={form.handoffTags} />
                </Field>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  A passagem para a equipe dispara o evento <code>conversation.handoff</code> nos webhooks da organização.
                </div>
              </div>
            )}
          </section>

          <section style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <h4 className="text-base font-semibold" style={{ fontSize: 15, marginBottom: 6 }}>Testar</h4>
            <div className="flex gap-2" style={{ alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <Field label="Mensagem do cliente">
                  <Input onChange={(e) => setTestText(e.target.value)} aria-label="Mensagem de teste" placeholder="Vocês entregam no sábado?" value={testText} />
                </Field>
              </div>
              <Button variant="secondary" loading={busy} disabled={!testText.trim()} onClick={simulate}>Simular</Button>
            </div>
            {testResult && (
              <div style={{ marginTop: 8, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {testResult.kind ? <><Badge variant="blue">{KIND_LABEL[testResult.kind]}</Badge> {testResult.reply}</> : 'Nenhuma resposta: a mensagem ficaria para a equipe.'}
                {testResult.error && <span style={{ color: 'var(--crm-text-error)' }}> (IA indisponível: {testResult.error})</span>}
              </div>
            )}
          </section>
        </div>
        <div style={{ marginTop: 20 }}>
          <label className="flex items-center gap-2">
            <Switch onCheckedChange={(checked: boolean) => setForm({ ...form, active: checked })} aria-label="Ativa" checked={form.active} />
            <div style={{ fontSize: 13 }}>Ativa</div>
          </label>
          </div>
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>
          Respostas automáticas
        </h2>
        <div style={{ color: 'var(--text-muted)', maxWidth: 780 }}>
          Quem escrever para a conexão fora de um fluxo recebe resposta na hora: por palavra-chave, uma mensagem fora do horário de atendimento e, se quiser, a IA respondendo com a sua base de conhecimento. Quando o contato pede uma pessoa, a IA avisa a equipe e fica em silêncio com ele.
        </div>
      </div>

      <div className="page-content space-y-4">
        {data.connections.length === 0 && (
          <Banner variant="default" description={<>Nenhuma conexão ainda. Conecte um número em WhatsApp › Conexões.</>} />
        )}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.connections.map((c) => {
            const cfg = c.config;
            const on = !!cfg?.active;
            const parts = cfg ? [
              (cfg.rules as Rule[] | undefined)?.length ? `${(cfg.rules as Rule[]).length} palavra${(cfg.rules as Rule[]).length > 1 ? 's' : ''}-chave` : null,
              cfg.awayEnabled ? 'fora do horário' : null,
              cfg.aiEnabled ? 'IA' : null,
            ].filter(Boolean) : [];
            const total = Object.values(c.last7d).reduce((a, b) => a + b, 0);
            return (
              <section key={c.name} className="brand-card" style={{ padding: 20 }}>
                <div className="flex items-start justify-between gap-3">
                  <div style={{ minWidth: 0 }}>
                    <h4 className="text-base font-semibold">{c.displayName || c.name}</h4>
                    <div className="flex items-center gap-1" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      <BrandLogo brand={providerBrand(c.provider)} size={14} rounded={3} />
                      {c.provider === 'META' ? 'API oficial' : c.provider === 'INSTAGRAM' ? 'Instagram' : c.provider} · {c.status === 'WORKING' || c.status === 'ACTIVE' ? 'conectada' : c.status.toLowerCase()}
                    </div>
                  </div>
                  <Badge variant={on ? 'green' : 'neutral'}>{on ? 'Ativa' : cfg ? 'Pausada' : 'Desligada'}</Badge>
                </div>
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  {parts.length ? `Responde por ${parts.join(', ')}.` : 'Sem respostas automáticas.'}
                </div>
                {total > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                    Últimos 7 dias: {Object.entries(c.last7d).map(([k, n]) => `${n} ${(KIND_LABEL[k] || k).toLowerCase()}`).join(' · ')}
                  </div>
                )}
                {c.interactiveCampaignEnabled && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                    Contatos dentro de um fluxo interativo seguem o fluxo.
                  </div>
                )}
                {canEdit && (
                  <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
                    {cfg
                      ? (
                        <label className="flex items-center gap-2">
                          <Switch onCheckedChange={(checked: boolean) => {
                              setForm({ ...DEFAULT, ...(cfg as Partial<Config>), rules: (cfg.rules as Rule[]) || [], window: (cfg.window as Config['window']) || DEFAULT.window, awayMessage: cfg.awayMessage || '', aiInstructions: cfg.aiInstructions || '', aiKnowledge: cfg.aiKnowledge || '' });
                              save({ ...(cfg as Partial<Config>), active: checked } as Partial<Config>, c);
                            }} aria-label="Ativa" checked={on} />
                          <div style={{ fontSize: 13 }}>Ativa</div>
                        </label>
                      )
                      : <span />}
                    <Button variant="secondary" icon={ICONS.Settings} onClick={() => open(c)}>Configurar</Button>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>


    </>
  );
}
